// ProfileFeed.tsx — Per-tab feed dispatcher for the profile page.
//
// Renders the active tab's content: posts / replies / media / likes / bookmarks.
// All data hooks are called unconditionally at the top of the component
// (React's rules of hooks — no hooks inside branches or conditionals).

import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import type { User } from '@/core/models';
import { postPath } from '@/core/lib/postUrl';
import { usePosts, useUserPosts, useProfileReplies, useBookmarksByType, useFollow } from '@/hooks';
import { PostCard } from '@/components/PostCard';
import { PostMedia } from '@/components/PostCard/PostMedia';
import { hasMedia } from '@/components/PostCard/postImages';
import { ProfileVideos } from './ProfileVideos';
import { Icon } from '@/icons';
import { IconButton } from '@/ui';
import { ReplyCard } from './ReplyCard';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface ProfileFeedProps {
  user: User;
  isOwn: boolean;
  active: string;
}

// ─── Styled primitives ────────────────────────────────────────────────────────

const FeedList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  margin-top: 12px;
`;

const EmptyStateBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 48px 16px;
  color: ${({ theme }) => theme.color.textMuted};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  text-align: center;
  background: ${({ theme }) => (theme.cardsGlass ? 'transparent' : theme.color.surface)};
  border-radius: ${({ theme }) => theme.radius.lg};
`;

const PrivacyHint = styled.p`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  margin: 8px 0 0;
  padding: 6px 10px;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => (theme.cardsGlass ? 'transparent' : theme.color.surface)};
`;

const MediaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
  margin-top: 12px;
`;

const MediaTile = styled(Link)`
  display: block;
  aspect-ratio: 1;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};

  /* PostMedia fills the tile */
  > * {
    width: 100%;
    height: 100%;
    pointer-events: none;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const MediaToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  margin-top: 12px;
`;

// ─── Shared empty state ───────────────────────────────────────────────────────

interface EmptyStateProps {
  icon: 'comment' | 'reply' | 'image' | 'heart' | 'cherish' | 'bookmark' | 'video';
  message: string;
}

function EmptyState({ icon, message }: EmptyStateProps) {
  return (
    <EmptyStateBlock>
      <Icon name={icon} size={28} />
      <span>{message}</span>
    </EmptyStateBlock>
  );
}

// ─── Media view toggle logic ──────────────────────────────────────────────────

type MediaView = 'grid' | 'feed';
const MEDIA_VIEW_KEY = 'truxsocial:profileMediaView';

function readMediaViewPref(): MediaView {
  try {
    const stored = localStorage.getItem(MEDIA_VIEW_KEY);
    if (stored === 'feed') return 'feed';
  } catch {
    // localStorage may be blocked (e.g. in tests with no mock)
  }
  return 'grid';
}

function writeMediaViewPref(v: MediaView): void {
  try {
    localStorage.setItem(MEDIA_VIEW_KEY, v);
  } catch {
    // ignore
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ProfileFeed({ user, isOwn, active }: ProfileFeedProps) {
  // All data hooks — unconditional, at the top.
  const { posts } = usePosts();
  // This author's own posts come from a real per-user fetch (not a filter of the
  // global feed, which only hydrates on Home) — so the Posts/Media tabs aren't
  // empty on a directly-loaded profile.
  const { posts: authorPosts, loading: authorPostsLoading } = useUserPosts(user.id);
  const { replies, replyCountFor } = useProfileReplies(user.id);
  const { bookmarks } = useBookmarksByType('post');
  const { isFollowing, isRequested } = useFollow(user.handle);

  // A private account's posts are gated server-side (visible_posts_for) — an
  // empty authorPosts list for a private, not-yet-approved viewer is
  // indistinguishable from "genuinely no posts" without this. Own profile is
  // never gated (isOwn short-circuits it here regardless of privateAccount).
  const isPrivateAndLocked = !isOwn && user.privateAccount === true && !isFollowing;

  // Media view toggle — pure view pref persisted to localStorage.
  const [mediaView, setMediaView] = useState<MediaView>(readMediaViewPref);

  function handleMediaViewChange(v: MediaView) {
    writeMediaViewPref(v);
    setMediaView(v);
  }

  // O(n) lookup map — avoids O(n²) posts.find() inside .map() calls below.
  const postsById = useMemo(() => new Map(posts.map((p) => [p.id, p])), [posts]);

  // Derived per-tab lists with memoisation.
  const mediaPosts = useMemo(() => authorPosts.filter((p) => hasMedia(p)), [authorPosts]);
  // 1.27.60: the server only returns the videos this viewer may watch — for a private
  // account's non-follower that is exactly its public videos (see visibility.py gate 4).
  const videoPosts = useMemo(() => authorPosts.filter((p) => p.video), [authorPosts]);

  const likedPosts = useMemo(
    () =>
      (user.likedPostIds ?? [])
        .map((id) => postsById.get(id))
        .filter((p): p is NonNullable<typeof p> => p !== undefined),
    [user.likedPostIds, postsById],
  );

  const bookmarkedPosts = useMemo(
    () =>
      bookmarks
        .map((b) => (b.postId !== null ? postsById.get(b.postId) : undefined))
        .filter((p): p is NonNullable<typeof p> => p !== undefined),
    [bookmarks, postsById],
  );

  // ── Tab: posts ──────────────────────────────────────────────────────────────
  if (active === 'posts') {
    if (isPrivateAndLocked) {
      return (
        <EmptyStateBlock>
          <Icon name="lock" size={28} />
          <span>This account is private.</span>
          <PrivacyHint>
            <Icon name="lock" size={14} />
            {isRequested
              ? 'Follow request sent. Waiting for approval.'
              : 'Follow requests must be approved before you can see their posts.'}
          </PrivacyHint>
          {videoPosts.length > 0 && (
            <PrivacyHint>
              <Icon name="video" size={14} />
              Their public videos are on the Videos tab.
            </PrivacyHint>
          )}
        </EmptyStateBlock>
      );
    }
    if (authorPosts.length === 0) {
      // Suppress the empty state until the fetch settles (avoids a flash).
      return authorPostsLoading ? null : <EmptyState icon="comment" message="No posts yet." />;
    }
    return (
      <FeedList>
        {authorPosts.map((p) => (
          <PostCard post={p} key={p.id} />
        ))}
      </FeedList>
    );
  }

  // ── Tab: replies ────────────────────────────────────────────────────────────
  if (active === 'replies') {
    if (replies.length === 0) {
      return <EmptyState icon="reply" message="No replies yet." />;
    }
    return (
      <FeedList>
        {replies.map((c) => (
          <ReplyCard
            key={c.id}
            comment={c}
            author={user}
            parentPost={postsById.get(c.postId) ?? null}
            parentAuthor={null}
            replyCount={replyCountFor(c.id)}
          />
        ))}
      </FeedList>
    );
  }

  // ── Tab: media ──────────────────────────────────────────────────────────────
  if (active === 'media') {
    return (
      <>
        <MediaToggleRow role="group" aria-label="Media view">
          <IconButton
            icon="layout-grid"
            label="Grid view"
            size={18}
            aria-pressed={mediaView === 'grid'}
            onClick={() => handleMediaViewChange('grid')}
          />
          <IconButton
            icon="list"
            label="Feed view"
            size={18}
            aria-pressed={mediaView === 'feed'}
            onClick={() => handleMediaViewChange('feed')}
          />
        </MediaToggleRow>

        {mediaPosts.length === 0 ? (
          authorPostsLoading ? null : (
            <EmptyState icon="image" message="No media yet." />
          )
        ) : mediaView === 'grid' ? (
          <MediaGrid>
            {/* postPath, not a hand-rolled `/post/${p.id}`: these are whole posts,
                so the link carries the share code AND the author's handle, and the
                post route shape lives in exactly one place (1.24.50). */}
            {mediaPosts.map((p) => (
              <MediaTile to={postPath(p)} key={p.id} aria-label={`Post by ${p.name}`}>
                <PostMedia post={p} fill />
              </MediaTile>
            ))}
          </MediaGrid>
        ) : (
          <FeedList>
            {mediaPosts.map((p) => (
              <PostCard post={p} key={p.id} />
            ))}
          </FeedList>
        )}
      </>
    );
  }

  // ── Tab: videos (1.27.60) ───────────────────────────────────────────────────
  if (active === 'videos') {
    if (videoPosts.length === 0) {
      if (authorPostsLoading) return null;
      return (
        <EmptyState
          icon="video"
          message={isPrivateAndLocked ? 'No public videos.' : 'No videos yet.'}
        />
      );
    }
    return <ProfileVideos posts={videoPosts} isOwn={isOwn} />;
  }

  // ── Tab: likes ──────────────────────────────────────────────────────────────
  if (active === 'likes') {
    const showPrivacyHint = isOwn && user.likesPublic !== true;
    return (
      <>
        {showPrivacyHint && (
          <PrivacyHint>
            <Icon name="lock" size={14} />
            Only you can see the posts you cherished. Change in Edit Profile
          </PrivacyHint>
        )}
        {likedPosts.length === 0 ? (
          <EmptyState icon="cherish" message="No cherished posts yet." />
        ) : (
          <FeedList>
            {likedPosts.map((p) => (
              <PostCard post={p} key={p.id} />
            ))}
          </FeedList>
        )}
      </>
    );
  }

  // ── Tab: bookmarks (own profile only) ───────────────────────────────────────
  if (active === 'bookmarks') {
    if (bookmarkedPosts.length === 0) {
      return <EmptyState icon="bookmark" message="No bookmarks yet." />;
    }
    return (
      <FeedList>
        {bookmarkedPosts.map((p) => (
          <PostCard post={p} key={p.id} />
        ))}
      </FeedList>
    );
  }

  // Unknown / out-of-range active — render nothing harmful.
  return null;
}
